import { el, append } from "../ui/dom";
import { signal, effect } from "../ui/signals";
import {
  pageHeader,
  prose,
  card,
  callout,
  codeBlock,
  rangeControl,
  stat,
  statRow,
  button,
} from "../ui/components";
import { plot, PALETTE } from "../ui/charts";
import { heatGrid, mixHex } from "../ui/grid";
import { themeColors, h2, legend, caption, ticker } from "./shared";
import { HyperLogLog } from "../core/hyperloglog";
import * as sizing from "../core/sizing";
import { formatBytes, formatCount, formatPercent } from "../ui/format";
import type { PageRenderer } from "../ui/router";

export const renderHyperLogLog: PageRenderer = (outlet) => {
  append(
    outlet,
    pageHeader({
      eyebrow: "Cardinality · count distinct",
      title: "HyperLogLog",
      lede: "Count the number of distinct items in a stream to within ~1% — using a few kilobytes, whether the true count is a thousand or a trillion. Feed a stream and watch the estimate lock onto the truth.",
    }),
  );

  append(
    outlet,
    prose(
      el(
        "p",
        {},
        "Hash each item to 64 bits. Use the first few bits to pick one of ",
        el("em", {}, "m"),
        " registers; in that register, remember the longest run of leading zeros seen in the rest of the bits. Long zero-runs are rare, so the biggest run across all registers hints at how many distinct values went in. Averaging the registers with a harmonic mean turns that hint into an estimate accurate to about ",
        el("strong", {}, "1.04 / √m"),
        ". Crucially, duplicates don't move the registers — so it counts ",
        el("em", {}, "distinct"),
        " items, not total.",
      ),
    ),
  );

  playgroundCard(outlet);
  calculatorCard(outlet);

  append(outlet, h2("Use it"));
  append(
    outlet,
    codeBlock(
      `import { HyperLogLog } from "./core";

const hll = new HyperLogLog({ precision: 14 }); // 2^14 registers ≈ 12 KB
for (const visitorId of requestStream) hll.add(visitorId);

hll.count();          // ≈ distinct visitors, ±0.8%
hll.standardError();  // 0.008  (1.04 / √16384)

// Unions are free: merge yesterday + today to get distinct-over-two-days.
const total = new HyperLogLog({ precision: 14 });
total.merge(yesterday);
total.merge(today);`,
    ),
  );

  append(
    outlet,
    callout(
      "info",
      el(
        "div",
        {},
        el("strong", {}, "Why engineers love it. "),
        "Fixed memory regardless of scale, and sketches ",
        el("strong", {}, "merge"),
        " by taking the element-wise max of registers — so you can compute distinct counts per shard and combine them, or roll hourly sketches into a daily one, with no re-scan. Redis' ",
        el("code", {}, "PFCOUNT"),
        " is exactly this, at precision 14.",
      ),
    ),
  );
};

// ---------------------------------------------------------------------------

function gridDims(m: number): { cols: number; rows: number; cell: number; gap: number } {
  const cols = m <= 32 ? m : m <= 1024 ? 64 : 128;
  const rows = Math.ceil(m / cols);
  const cell = Math.min(18, Math.max(3, Math.floor(560 / cols)));
  return { cols, rows, cell, gap: cell >= 8 ? 2 : 1 };
}

function playgroundCard(outlet: HTMLElement): void {
  const pSig = signal(10); // precision
  const traceKey = signal("");
  const { tick, bump } = ticker();

  let hll = new HyperLogLog({ precision: pSig.value });
  let n = 0; // distinct items added (they are "v0".."v(n-1)")
  const points: Array<[number, number]> = []; // (true, estimate)
  let grid = heatGrid(1, 1);
  const gridHost = el("div", { class: "viz-wrap" });

  function rebuildGrid(): void {
    const { cols, rows, cell, gap } = gridDims(hll.m);
    grid = heatGrid(cols, rows, { cellSize: cell, gap });
    gridHost.replaceChildren(grid.canvas);
  }

  // Precision change → fresh sketch, fresh grid, cleared run.
  effect(() => {
    hll = new HyperLogLog({ precision: pSig.value });
    n = 0;
    points.length = 0;
    rebuildGrid();
    bump();
  });

  function feed(count: number, steps: number): void {
    const per = Math.max(1, Math.floor(count / steps));
    for (let s = 0; s < steps; s++) {
      for (let i = 0; i < per; i++) hll.add(`v${n++}`);
      points.push([n, hll.count()]);
    }
    bump();
  }

  const chartHost = el("div", { class: "viz-wrap" });

  // Redraw register heatmap + convergence chart.
  effect(() => {
    tick.value;
    const c = themeColors();
    let max = 8;
    for (let i = 0; i < hll.registers.length; i++) max = Math.max(max, hll.registers[i]);
    const traced = traceKey.value ? hll.trace(traceKey.value).index : -1;
    grid.draw((i) => {
      if (i === traced) return c.accent2;
      const v = hll.registers[i];
      if (v === 0) return c.bg2;
      return mixHex(c.border, c.accent, Math.min(1, v / max));
    });

    const maxN = Math.max(10, n);
    chartHost.replaceChildren(
      plot({
        height: 300,
        xScale: "log",
        yScale: "log",
        xLabel: "true distinct count",
        yLabel: "estimated count",
        xFormat: formatCount,
        yFormat: formatCount,
        series: [
          { type: "line", points: [[1, 1], [maxN, maxN]], color: c.textFaint, dashed: true, label: "perfect" },
          { type: "scatter", points, color: PALETTE[0], radius: 3.5, label: "HyperLogLog" },
        ],
        legend: true,
      }),
    );
  });

  const traceInfo = el("div", { class: "mini-note", style: { minHeight: "1.3em" } });
  effect(() => {
    const k = traceKey.value.trim();
    if (!k) {
      traceInfo.textContent = "";
      return;
    }
    const t = hll.trace(k);
    traceInfo.textContent = `“${k}” → register #${t.index}, leading-zero run ρ = ${t.rho}`;
  });

  const controls = el(
    "div",
    { class: "lab-controls" },
    rangeControl({
      label: "precision p",
      min: 4,
      max: 14,
      signal: pSig,
      format: (p) => `${p}  (m = ${formatCount(2 ** p)})`,
      hint: "more registers → less error, more memory",
    }),
    el(
      "div",
      { class: "button-row" },
      button({ label: "+1K", variant: "ghost", onClick: () => feed(1000, 5) }),
      button({ label: "+10K", variant: "ghost", onClick: () => feed(10000, 10) }),
      button({ label: "+100K", variant: "primary", onClick: () => feed(100000, 20) }),
    ),
    button({
      label: "Reset",
      variant: "ghost",
      onClick: () => {
        hll = new HyperLogLog({ precision: pSig.value });
        n = 0;
        points.length = 0;
        bump();
      },
    }),
    el(
      "label",
      { class: "control" },
      el("span", { class: "control-label" }, "Trace a key"),
      el("input", {
        class: "textinput",
        type: "text",
        placeholder: "which register does 'hello' hit?",
        oninput: (e: Event) => (traceKey.value = (e.target as HTMLInputElement).value),
      }),
    ),
    traceInfo,
    statRow(
      stat({ label: "True distinct", value: () => (tick.value, formatCount(n)) }),
      stat({ label: "Estimate", accent: true, value: () => (tick.value, formatCount(hll.count())) }),
    ),
    statRow(
      stat({
        label: "Error",
        value: () => (tick.value, n === 0 ? "—" : formatPercent(Math.abs(hll.count() - n) / n)),
      }),
      stat({ label: "Memory", value: () => formatBytes(sizing.hllMemoryBytes(pSig.value)), hint: "6-bit registers" }),
    ),
  );

  const viz = el(
    "div",
    {},
    gridHost,
    legend([
      [themeColors().accent, "register (bigger run = brighter)"],
      [themeColors().accent2, "traced key"],
    ]),
    caption("Each cell is one register. Feed items and watch the estimate track the diagonal across orders of magnitude."),
    chartHost,
  );

  append(
    outlet,
    card({
      title: "Feed a stream, watch it count",
      subtitle: "The estimate should hug the diagonal as the true count grows — even though memory never does.",
      children: [el("div", { class: "lab-layout" }, controls, viz)],
    }),
  );
}

// ---------------------------------------------------------------------------

function calculatorCard(outlet: HTMLElement): void {
  const errPct = signal(1); // target error, in percent
  const chartHost = el("div", { class: "viz-wrap" });

  const precisionFor = () => sizing.hllPrecisionForError(errPct.value / 100);

  effect(() => {
    const p = precisionFor();
    const errCurve: Array<[number, number]> = [];
    for (let pp = 4; pp <= 18; pp++) errCurve.push([pp, sizing.hllStandardError(pp)]);
    chartHost.replaceChildren(
      plot({
        height: 280,
        yScale: "log",
        xLabel: "precision p",
        yLabel: "standard error",
        yFormat: (v) => formatPercent(v),
        xFormat: (v) => String(v),
        series: [
          { type: "line", points: errCurve, color: PALETTE[1], label: "std error" },
          { type: "scatter", points: [[p, sizing.hllStandardError(p)]], color: PALETTE[0], radius: 5 },
        ],
        legend: true,
      }),
    );
  });

  const controls = el(
    "div",
    { class: "lab-controls" },
    rangeControl({
      label: "target error",
      min: 0.2,
      max: 10,
      step: 0.1,
      signal: errPct,
      format: (v) => `±${v}%`,
    }),
    statRow(
      stat({ label: "Precision", value: () => String(precisionFor()) }),
      stat({ label: "Registers", value: () => formatCount(2 ** precisionFor()) }),
    ),
    stat({
      label: "Memory",
      accent: true,
      value: () => formatBytes(sizing.hllMemoryBytes(precisionFor())),
      hint: "same size for a thousand or a trillion items",
    }),
    el(
      "p",
      { class: "mini-note" },
      "Standard error is 1.04 / √m — it depends only on the register count, never on how many items you count.",
    ),
  );

  append(
    outlet,
    card({
      title: "Size it for an accuracy target",
      subtitle: "Pick the error you can tolerate; get the precision and the (fixed) memory it needs.",
      children: [el("div", { class: "lab-layout" }, controls, chartHost)],
    }),
  );
}
